// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Effort Engine Page
// Route: /effort
// apps/web/src/routes/effort.tsx
//
// The novel core of ForgeTrack. Visualizes the 6-multiplier equivalent
// effort formula, manages industry benchmarks, runs AI-assisted estimation,
// and allows manual override. Every multiplier is explorable.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  NeonValue,
  EngineTooltip,
  EmptyState,
  LoadingShimmer,
  StatusBadge,
} from '../components/engine/EnginePageShell';
import { useCounter } from '../components/engine/hooks';
import { useBenchmarks, useEffortMutations } from '../lib/api/effort-reports';
import { useProjects, useWorkItems } from '../lib/api/projects';
import type { CalculationResult, AIEstimateResult } from '../lib/api/effort-reports';
import type { WorkItem } from '../lib/api/types';

export const Route = createFileRoute('/effort')({
  component: EffortEnginePage,
});

// ─── Multiplier definitions ─────────────────────────────────────────────────

const MULTIPLIERS = [
  { key: 'architecturalDepthMultiplier', label: 'Architectural Depth', range: '1.0 – 4.0', color: C.violet, icon: '🏗',
    desc: 'Layers of abstraction, design patterns, system boundaries. Higher = more sophisticated architecture.' },
  { key: 'integrationMultiplier', label: 'Integration Points', range: '1.0 + 0.3/pt', color: C.cyan, icon: '🔗',
    desc: 'External systems, APIs, databases connected. Each integration adds 30% complexity.' },
  { key: 'domainExpertiseMultiplier', label: 'Domain Expertise', range: '1.0 – 3.0', color: C.amber, icon: '🎯',
    desc: 'Industry-specific knowledge required (lending, healthcare, etc.). Higher = more specialized.' },
  { key: 'infrastructureExpertiseMultiplier', label: 'Infrastructure', range: '1.0 – 3.0', color: C.blue, icon: '☁',
    desc: 'Cloud architecture, deployment, scaling, monitoring. Enterprise-grade = 3.0.' },
  { key: 'securityExpertiseMultiplier', label: 'Security', range: '1.0 – 2.5', color: C.coral, icon: '🔒',
    desc: 'Auth, encryption, compliance, RBAC. CISSP-level architecture = 2.5.' },
  { key: 'noveltyMultiplier', label: 'Novelty', range: '1.0 – 3.0', color: C.pink, icon: '✨',
    desc: 'How novel the approach is. Known pattern = 1.0, no prior implementation = 3.0.' },
] as const;

// ─── Main Component ─────────────────────────────────────────────────────────

function EffortEnginePage() {
  const { data: benchmarks, loading: benchLoading } = useBenchmarks();
  const { data: projects } = useProjects();
  const mutations = useEffortMutations();

  const [selectedProjectId, setSelectedProjectId] = useState<number>(0);
  const [selectedWorkItemId, setSelectedWorkItemId] = useState<number>(0);
  const [calcResult, setCalcResult] = useState<CalculationResult | null>(null);
  const [aiResult, setAIResult] = useState<AIEstimateResult | null>(null);
  const [showBenchmarkForm, setShowBenchmarkForm] = useState(false);

  const { data: workItems } = useWorkItems(selectedProjectId);

  const selectedWorkItem = useMemo(
    () => workItems?.find(w => w.id === selectedWorkItemId) || null,
    [workItems, selectedWorkItemId]
  );

  // ── Benchmark metrics ──────────────────────────────────────────────────

  const benchMetrics = useMemo(() => {
    if (!benchmarks) return null;
    const global = benchmarks.filter(b => b.isGlobal).length;
    const custom = benchmarks.filter(b => !b.isGlobal).length;
    const avgWeeks = benchmarks.length > 0
      ? benchmarks.reduce((s, b) => s + b.industryBenchmarkWeeks, 0) / benchmarks.length
      : 0;
    return { total: benchmarks.length, global, custom, avgWeeks };
  }, [benchmarks]);

  const countBenchmarks = useCounter(benchMetrics?.total ?? 0, 500, 200);

  // ── Run calculation ────────────────────────────────────────────────────

  const handleCalculate = async () => {
    if (!selectedWorkItemId) return;
    const result = await mutations.calculate(selectedWorkItemId);
    if (result) setCalcResult(result);
  };

  const handleAIEstimate = async () => {
    if (!selectedWorkItemId) return;
    const result = await mutations.aiEstimate(selectedWorkItemId);
    if (result) setAIResult(result);
  };

  return (
    <EnginePageShell
      title="Effort Engine"
      subtitle="EQUIVALENT EFFORT CALCULATOR"
      icon="⚙"
      accentColor={C.violet}
      statusLabel="ENGINE READY"
      statusColor={C.violet}
    >
      {/* ══════════ FORMULA DISPLAY ══════════ */}
      <GlassPanel accent={C.violet} delay={0.1} style={{ marginBottom: 20 }}>
        <SectionLabel color={C.violet}>The Formula</SectionLabel>
        <div style={{
          fontFamily: F.mono, fontSize: 12, color: C.textSoft, lineHeight: 2,
          padding: '12px 16px', background: 'rgba(0,0,0,0.3)', borderRadius: 8,
          overflowX: 'auto', whiteSpace: 'nowrap',
        }}>
          <span style={{ color: C.neon }}>EquivalentWeeks</span>
          <span style={{ color: C.dim }}> = </span>
          <span style={{ color: C.textSoft }}>Base</span>
          {MULTIPLIERS.map(m => (
            <span key={m.key}>
              <span style={{ color: C.dim }}> × </span>
              <EngineTooltip content={<div><div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>{m.label}</div><div>{m.desc}</div><div style={{ marginTop: 4, fontFamily: F.mono, color: m.color }}>Range: {m.range}</div></div>}>
                <span style={{ color: m.color, cursor: 'pointer', borderBottom: `1px dashed ${m.color}40` }}>{m.label.split(' ')[0]}</span>
              </EngineTooltip>
            </span>
          ))}
          <span style={{ color: C.dim }}> + </span>
          <EngineTooltip content={<div>Years of training per domain × ramp-up cost, prorated across items.</div>}>
            <span style={{ color: C.amber, cursor: 'pointer', borderBottom: `1px dashed ${C.amber}40` }}>ExpertiseOverhead</span>
          </EngineTooltip>
          <span style={{ color: C.dim }}> + </span>
          <EngineTooltip content={<div>(ImpliedTeam − 1) × 0.15 × Base — the communication tax per person.</div>}>
            <span style={{ color: C.cyan, cursor: 'pointer', borderBottom: `1px dashed ${C.cyan}40` }}>CoordinationOverhead</span>
          </EngineTooltip>
        </div>
      </GlassPanel>

      {/* ══════════ CALCULATOR ══════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {/* Left — Work item selector + run */}
        <div>
          <SectionLabel color={C.cyan}>Calculate Effort</SectionLabel>
          <GlassPanel accent={C.cyan} delay={0.2}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Project selector */}
              <div>
                <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>SELECT PROJECT</div>
                <select
                  value={selectedProjectId}
                  onChange={(e) => { setSelectedProjectId(Number(e.target.value)); setSelectedWorkItemId(0); setCalcResult(null); setAIResult(null); }}
                  style={{
                    width: '100%', background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`,
                    borderRadius: 8, padding: '10px 14px', color: C.text, fontFamily: F.display,
                    fontSize: 13, outline: 'none', appearance: 'none' as const,
                  }}
                >
                  <option value={0}>Choose a project...</option>
                  {projects?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {selectedProjectId > 0 && (
                  <button
                    onClick={async () => { await mutations.recalcProject(selectedProjectId); }}
                    disabled={mutations.loading}
                    style={{
                      marginTop: 6, padding: '6px 14px', borderRadius: 6, fontSize: 11,
                      fontWeight: 600, fontFamily: F.mono, cursor: 'pointer',
                      background: `${C.amber}15`, color: C.amber,
                      border: `1px solid ${C.amber}40`,
                    }}
                  >
                    {mutations.loading ? '◌ Recalculating...' : '⚡ Recalculate All Work Items'}
                  </button>
                )}
              </div>

              {/* Work item selector */}
              {selectedProjectId > 0 && (
                <div>
                  <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>SELECT WORK ITEM</div>
                  <select
                    value={selectedWorkItemId}
                    onChange={(e) => { setSelectedWorkItemId(Number(e.target.value)); setCalcResult(null); setAIResult(null); }}
                    style={{
                      width: '100%', background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`,
                      borderRadius: 8, padding: '10px 14px', color: C.text, fontFamily: F.display,
                      fontSize: 13, outline: 'none', appearance: 'none' as const,
                    }}
                  >
                    <option value={0}>Choose a work item...</option>
                    {workItems?.map(w => <option key={w.id} value={w.id}>{w.name} (complexity: {(w.complexityScore ?? 0).toFixed(1)})</option>)}
                  </select>
                </div>
              )}

              {/* Selected work item complexity preview */}
              {selectedWorkItem && (
                <div style={{ padding: 12, background: 'rgba(0,0,0,0.2)', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 }}>{selectedWorkItem.name}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    {MULTIPLIERS.map(m => {
                      const dimKey = m.key.replace('Multiplier', '').replace('expertise', 'Expertise');
                      // Map multiplier key back to work item property
                      const propMap: Record<string, keyof WorkItem> = {
                        architecturalDepth: 'architecturalDepth',
                        integration: 'integrationPoints',
                        domainExpertise: 'domainExpertise',
                        infrastructureExpertise: 'infrastructureExpertise',
                        securityExpertise: 'securityExpertise',
                        novelty: 'novelty',
                      };
                      const prop = propMap[dimKey] || dimKey;
                      const val = (selectedWorkItem as any)[prop] ?? 0;
                      return (
                        <div key={m.key} style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.08em' }}>{m.label.split(' ')[0].toUpperCase()}</div>
                          <div style={{ fontSize: 16, fontFamily: F.mono, fontWeight: 700, color: m.color }}>{val}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  disabled={!selectedWorkItemId || mutations.loading}
                  onClick={handleCalculate}
                  style={{
                    flex: 1, background: selectedWorkItemId ? `${C.neon}15` : 'transparent',
                    border: `1px solid ${selectedWorkItemId ? `${C.neon}40` : C.faint}`,
                    color: selectedWorkItemId ? C.neon : C.dim,
                    fontFamily: F.mono, fontSize: 11, fontWeight: 600, padding: '10px 16px',
                    borderRadius: 8, cursor: selectedWorkItemId ? 'pointer' : 'not-allowed',
                    opacity: mutations.loading ? 0.6 : 1,
                  }}
                >
                  ⚙ FORMULA CALC
                </button>
                <button
                  disabled={!selectedWorkItemId || mutations.loading}
                  onClick={handleAIEstimate}
                  style={{
                    flex: 1, background: selectedWorkItemId ? `${C.violet}15` : 'transparent',
                    border: `1px solid ${selectedWorkItemId ? `${C.violet}40` : C.faint}`,
                    color: selectedWorkItemId ? C.violet : C.dim,
                    fontFamily: F.mono, fontSize: 11, fontWeight: 600, padding: '10px 16px',
                    borderRadius: 8, cursor: selectedWorkItemId ? 'pointer' : 'not-allowed',
                    opacity: mutations.loading ? 0.6 : 1,
                  }}
                >
                  🤖 AI ESTIMATE
                </button>
              </div>

              {mutations.error && (
                <div style={{ fontSize: 12, color: C.coral, padding: '8px 12px', background: `${C.coral}10`, borderRadius: 6, border: `1px solid ${C.coral}20` }}>
                  {mutations.error.message}
                </div>
              )}
            </div>
          </GlassPanel>
        </div>

        {/* Right — Results */}
        <div>
          <SectionLabel color={C.neon}>Results</SectionLabel>
          {calcResult ? (
            <GlassPanel accent={C.neon} delay={0}>
              <FormulaResultPanel result={calcResult} />
              {selectedWorkItemId > 0 && (
                <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <OverrideButton workItemId={selectedWorkItemId} currentWeeks={calcResult.totalEquivalentWeeks} mutations={mutations} onDone={() => {}} />
                </div>
              )}
            </GlassPanel>
          ) : aiResult ? (
            <GlassPanel accent={C.violet} delay={0}>
              <AIResultPanel result={aiResult} />
              {selectedWorkItemId > 0 && (
                <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <OverrideButton workItemId={selectedWorkItemId} currentWeeks={aiResult.estimatedWeeks ?? 0} mutations={mutations} onDone={() => {}} />
                </div>
              )}
            </GlassPanel>
          ) : (
            <GlassPanel accent={C.dim} delay={0.3}>
              <EmptyState
                icon="⚙"
                title="Select & calculate"
                subtitle="Choose a project and work item, then run the formula calculation or AI estimation to see equivalent effort breakdown."
              />
            </GlassPanel>
          )}
        </div>
      </div>

      {/* ══════════ BENCHMARKS ══════════ */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <SectionLabel color={C.amber}>
          Industry Benchmarks ({countBenchmarks})
        </SectionLabel>
        <button
          onClick={() => setShowBenchmarkForm(!showBenchmarkForm)}
          style={{
            background: `${C.amber}12`, border: `1px solid ${C.amber}30`, color: C.amber,
            fontFamily: F.mono, fontSize: 10, padding: '5px 14px', borderRadius: 6, cursor: 'pointer',
          }}
        >
          + ADD BENCHMARK
        </button>
      </div>

      {benchLoading ? (
        <GlassPanel delay={0.4}><LoadingShimmer height={120} /></GlassPanel>
      ) : benchmarks && benchmarks.length > 0 ? (
        <GlassPanel accent={C.amber} delay={0.4} style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['CATEGORY', 'WEEKS', 'TEAM', 'MARKET COST', 'SOURCE', 'SCOPE'].map(h => (
                    <th key={h} style={{
                      fontSize: 9, fontFamily: F.mono, letterSpacing: '0.1em', color: C.dim,
                      padding: '10px 14px', borderBottom: `1px solid ${C.faint}`, textAlign: h === 'CATEGORY' ? 'left' : 'right',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {benchmarks.map((b, i) => (
                  <tr key={b.id} style={{
                    borderBottom: `1px solid ${C.faint}`,
                    opacity: 0, animation: `engine-feed-in 0.3s ease ${0.5 + i * 0.03}s forwards`,
                  }}>
                    <td style={{ padding: '10px 14px', fontSize: 13, color: C.text, fontWeight: 500 }}>{b.category}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: F.mono, fontSize: 13, color: C.violet, fontWeight: 600 }}>
                      {b.industryBenchmarkWeeks}<span style={{ fontSize: 9, color: C.dim }}> wks</span>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 12, color: C.muted }}>{b.teamSizeAssumed || '—'}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: F.mono, fontSize: 13, color: C.neon }}>
                      {b.marketRateCost > 0 ? `$${(b.marketRateCost / 1000).toFixed(0)}K` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, color: C.dim }}>{b.benchmarkSource || '—'}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <StatusBadge label={b.isGlobal ? 'GLOBAL' : 'CUSTOM'} color={b.isGlobal ? C.cyan : C.amber} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassPanel>
      ) : (
        <GlassPanel accent={C.amber} delay={0.4}>
          <EmptyState icon="📊" title="No benchmarks" subtitle="Industry benchmarks provide the baseline for equivalent effort calculations. Pre-loaded benchmarks are added during database setup." />
        </GlassPanel>
      )}
    </EnginePageShell>
  );
}

// ─── FormulaResultPanel ─────────────────────────────────────────────────────

function FormulaResultPanel({ result }: { result: CalculationResult }) {
  const countWeeks = useCounter(Math.round(result.totalEquivalentWeeks * 10) / 10, 800, 100);
  const countCost = useCounter(Math.round(result.marketRateCost / 1000), 800, 200);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.12em', marginBottom: 4 }}>EQUIVALENT WEEKS</div>
          <NeonValue color={C.neon} size={36} glow>{countWeeks}</NeonValue>
        </div>
        <div style={{ width: 1, height: 40, background: C.faint }} />
        <div>
          <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.12em', marginBottom: 4 }}>MARKET COST</div>
          <NeonValue color={C.neon} size={28}>${countCost}K</NeonValue>
        </div>
        <div style={{ width: 1, height: 40, background: C.faint }} />
        <div>
          <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.12em', marginBottom: 4 }}>IMPLIED TEAM</div>
          <NeonValue color={C.cyan} size={28}>{result.impliedTeamSize}</NeonValue>
          <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim }}> people</span>
        </div>
      </div>

      {/* Multiplier breakdown */}
      <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 8 }}>MULTIPLIER BREAKDOWN</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {MULTIPLIERS.map(m => {
          const val = (result as any)[m.key] as number || 1;
          const pct = Math.min(((val - 1) / 3) * 100, 100);
          return (
            <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12 }}>{m.icon}</span>
              <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim, width: 100 }}>{m.label.split(' ')[0]}</span>
              <div style={{ flex: 1, height: 6, background: C.faint, borderRadius: 3 }}>
                <div style={{
                  width: `${pct}%`, height: '100%', background: m.color, borderRadius: 3,
                  animation: 'engine-bar-fill 0.6s cubic-bezier(0.16,1,0.3,1)',
                  boxShadow: `0 0 6px ${m.color}40`,
                }} />
              </div>
              <span style={{ fontSize: 12, fontFamily: F.mono, fontWeight: 600, color: m.color, minWidth: 36, textAlign: 'right' }}>
                {val.toFixed(2)}×
              </span>
            </div>
          );
        })}
      </div>

      {/* Overheads */}
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.faint}`, display: 'flex', gap: 20, fontSize: 11, fontFamily: F.mono }}>
        <div>
          <span style={{ color: C.dim }}>Expertise Overhead: </span>
          <span style={{ color: C.amber }}>{result.expertiseAcquisitionOverhead.toFixed(1)} wks</span>
        </div>
        <div>
          <span style={{ color: C.dim }}>Coordination Overhead: </span>
          <span style={{ color: C.cyan }}>{result.coordinationOverhead.toFixed(1)} wks</span>
        </div>
        <div>
          <span style={{ color: C.dim }}>Base: </span>
          <span style={{ color: C.textSoft }}>{result.baseWeeks.toFixed(1)} wks</span>
        </div>
      </div>
    </div>
  );
}

// ─── AIResultPanel ──────────────────────────────────────────────────────────

function AIResultPanel({ result }: { result: AIEstimateResult }) {
  const countWeeks = useCounter(Math.round(result.estimatedWeeks * 10) / 10, 800, 100);
  const confidencePct = Math.round(result.confidence * 100);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 18 }}>🤖</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.violet }}>AI Estimation</span>
        <StatusBadge label={result.aiModel} color={C.violet} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.12em', marginBottom: 4 }}>ESTIMATED WEEKS</div>
          <NeonValue color={C.violet} size={36} glow>{countWeeks}</NeonValue>
        </div>
        <div style={{ width: 1, height: 40, background: C.faint }} />
        <div>
          <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.12em', marginBottom: 4 }}>CONFIDENCE</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <NeonValue color={confidencePct > 70 ? C.neon : confidencePct > 40 ? C.amber : C.coral} size={28}>
              {confidencePct}%
            </NeonValue>
            {/* Mini confidence bar */}
            <div style={{ width: 60, height: 6, background: C.faint, borderRadius: 3 }}>
              <div style={{
                width: `${confidencePct}%`, height: '100%', borderRadius: 3,
                background: confidencePct > 70 ? C.neon : confidencePct > 40 ? C.amber : C.coral,
                animation: 'engine-bar-fill 0.6s ease',
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* Rationale */}
      {result.rationale && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 9, fontFamily: F.mono, color: C.violet, letterSpacing: '0.1em', marginBottom: 4 }}>RATIONALE</div>
          <div style={{ fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>{result.rationale}</div>
        </div>
      )}

      {/* Comparable projects */}
      {result.comparableProjects && result.comparableProjects.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 9, fontFamily: F.mono, color: C.cyan, letterSpacing: '0.1em', marginBottom: 4 }}>COMPARABLE PROJECTS</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {result.comparableProjects.map((p, i) => (
              <span key={i} style={{
                fontSize: 10, fontFamily: F.mono, color: C.cyan, background: `${C.cyan}10`,
                border: `1px solid ${C.cyan}20`, padding: '3px 10px', borderRadius: 4,
              }}>{p}</span>
            ))}
          </div>
        </div>
      )}

      {/* Cost */}
      <div style={{ fontSize: 10, fontFamily: F.mono, color: C.dim, paddingTop: 8, borderTop: `1px solid ${C.faint}` }}>
        {result.tokensUsed.toLocaleString()} tokens · ${result.cost.toFixed(4)} cost
      </div>
    </div>
  );
}

// ── Override Button ──────────────────────────────────────────────────────────

function OverrideButton({ workItemId, currentWeeks, mutations, onDone }: {
  workItemId: number;
  currentWeeks: number;
  mutations: ReturnType<typeof useEffortMutations>;
  onDone: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [weeks, setWeeks] = useState(String(Math.round(currentWeeks * 10) / 10));

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        style={{
          padding: '5px 14px', borderRadius: 6, fontSize: 11,
          fontWeight: 600, fontFamily: F.mono, cursor: 'pointer',
          background: `${C.cyan}15`, color: C.cyan,
          border: `1px solid ${C.cyan}40`,
        }}
      >✏️ Override</button>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input
        type="number"
        step="0.1"
        min="0"
        value={weeks}
        onChange={(e) => setWeeks(e.target.value)}
        style={{
          width: 80, padding: '5px 8px', borderRadius: 6, fontSize: 12,
          background: C.bg, color: C.text, border: `1px solid ${C.faint}`,
          fontFamily: F.mono, outline: 'none',
        }}
      />
      <span style={{ fontSize: 10, color: C.dim }}>weeks</span>
      <button
        onClick={async () => {
          const ok = await mutations.override(workItemId, parseFloat(weeks));
          if (ok) { setEditing(false); onDone(); }
        }}
        disabled={mutations.loading}
        style={{
          padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
          background: `${C.neon}20`, color: C.neon,
          border: `1px solid ${C.neon}40`, cursor: 'pointer', fontFamily: F.mono,
        }}
      >Save</button>
      <button
        onClick={() => setEditing(false)}
        style={{
          padding: '5px 10px', borderRadius: 6, fontSize: 11,
          background: 'none', color: C.muted, border: `1px solid ${C.faint}`,
          cursor: 'pointer', fontFamily: F.mono,
        }}
      >✕</button>
    </div>
  );
}
